import { useEffect, useMemo, useState } from "react";
import type { ReportTileMeta } from "../reports";

/**
 * Paginated (export-to-PDF) report view — the A-lite distribution mechanism
 * (G005 decisions.md 2026-06-10). This component owns ONLY the parameter form,
 * the Generate action, and the inline PDF preview/download. It deliberately
 * does NOT touch the interactive embed path — the seam that keeps a future
 * live-embed viewer (A-hybrid) additive.
 *
 * "View before sending" = previewing the actual generated PDF inline. Changing
 * a filter = adjust the form and Generate again (regenerate).
 */
type GenState =
  | { kind: "idle" }
  | { kind: "generating" }
  | { kind: "preview"; url: string; filename: string }
  | { kind: "stub" }
  | { kind: "error"; message: string };

export default function PaginatedReport({ report }: { report: ReportTileMeta }) {
  const params = report.paginatedParameters ?? [];

  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const p of params) init[p.name] = p.defaultValue ?? "";
    return init;
  });
  const [state, setState] = useState<GenState>({ kind: "idle" });

  // Revoke the blob URL when leaving a preview (regenerate or unmount) so we
  // don't leak object URLs.
  useEffect(() => {
    return () => {
      if (state.kind === "preview") URL.revokeObjectURL(state.url);
    };
  }, [state]);

  const missingRequired = useMemo(
    () => params.filter(p => p.required && (values[p.name] ?? "").trim() === "").map(p => p.label),
    [params, values]
  );

  // Dynamic option sources (e.g. Project). Fetched once on mount; on failure the
  // field falls back to its static input type so the report stays usable.
  const needsProjects = params.some(p => p.optionsSource === "projects");
  const [projOptions, setProjOptions] = useState<{ status: "loading" | "ready" | "failed"; list: string[] }>({
    status: needsProjects ? "loading" : "ready",
    list: []
  });

  useEffect(() => {
    if (!needsProjects) return;
    let cancelled = false;
    setProjOptions({ status: "loading", list: [] });
    (async () => {
      try {
        const r = await fetch("/api/projects");
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const data = (await r.json()) as { projects?: string[] };
        if (!cancelled) setProjOptions({ status: "ready", list: data.projects ?? [] });
      } catch {
        if (!cancelled) setProjOptions({ status: "failed", list: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [needsProjects]);

  function setValue(name: string, v: string) {
    setValues(prev => ({ ...prev, [name]: v }));
  }

  async function onGenerate() {
    if (missingRequired.length > 0) return;
    setState({ kind: "generating" });
    try {
      const resp = await fetch("/api/export-paginated", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ report: report.slug, parameters: values })
      });

      if (resp.status === 503) {
        setState({ kind: "stub" });
        return;
      }
      if (!resp.ok) {
        let msg = `HTTP ${resp.status}`;
        try {
          const j = await resp.json();
          if (j?.error) msg = j.error;
        } catch {
          /* non-JSON error body */
        }
        setState({ kind: "error", message: msg });
        return;
      }

      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const stamp = params
        .map(p => (values[p.name] ?? "").trim())
        .filter(Boolean)
        .join("_")
        .replace(/[^a-z0-9_]+/gi, "-")
        .replace(/-+/g, "-")
        .replace(/^[-_]+|[-_]+$/g, "");
      const base =
        report.name.replace(/[^a-z0-9]+/gi, "_").replace(/^_+|_+$/g, "") || "report";
      const filename = stamp ? `${base}_${stamp}.pdf` : `${base}.pdf`;
      setState({ kind: "preview", url, filename });
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  }

  const inputStyle: React.CSSProperties = {
    padding: "0.45rem 0.55rem",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "0.9rem",
    fontFamily: "inherit",
    minWidth: "220px"
  };
  const labelStyle: React.CSSProperties = {
    fontSize: "0.85rem",
    color: "#444",
    fontWeight: 600,
    marginBottom: "0.25rem",
    display: "block"
  };

  return (
    <section
      style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
      data-testid="paginated-report"
    >
      <form
        data-testid="paginated-form"
        onSubmit={e => {
          e.preventDefault();
          void onGenerate();
        }}
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "flex-end",
          padding: "0.75rem 0",
          borderBottom: "1px solid #eee"
        }}
      >
        {params.map(p => (
          <div key={p.name} style={{ display: "flex", flexDirection: "column" }}>
            <label style={labelStyle} htmlFor={`param-${p.name}`}>
              {p.label}
              {p.required && <span style={{ color: "#b00020" }}> *</span>}
            </label>
            {p.optionsSource === "projects" ? (
              projOptions.status === "loading" ? (
                <select id={`param-${p.name}`} data-testid="project-loading" disabled style={inputStyle}>
                  <option>Loading projects…</option>
                </select>
              ) : projOptions.status === "ready" ? (
                <select
                  id={`param-${p.name}`}
                  data-testid="project-select"
                  value={values[p.name] ?? ""}
                  onChange={e => setValue(p.name, e.target.value)}
                  style={inputStyle}
                >
                  <option value="">— Select —</option>
                  {projOptions.list.map(name => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : (
                // Fetch failed — fall back to a text input so the report still works.
                <input
                  id={`param-${p.name}`}
                  data-testid={`paginated-param-${p.name}`}
                  type="text"
                  placeholder="Type the exact project name"
                  value={values[p.name] ?? ""}
                  onChange={e => setValue(p.name, e.target.value)}
                  style={inputStyle}
                />
              )
            ) : p.type === "select" ? (
              <select
                id={`param-${p.name}`}
                data-testid={`paginated-param-${p.name}`}
                value={values[p.name] ?? ""}
                onChange={e => setValue(p.name, e.target.value)}
                style={inputStyle}
              >
                <option value="">— Select —</option>
                {(p.options ?? []).map(o => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            ) : p.type === "integer" ? (
              <input
                id={`param-${p.name}`}
                data-testid={`paginated-param-${p.name}`}
                type="number"
                step={1}
                min={p.min}
                max={p.max}
                value={values[p.name] ?? ""}
                onChange={e => setValue(p.name, e.target.value)}
                style={{ ...inputStyle, minWidth: "120px" }}
              />
            ) : (
              <input
                id={`param-${p.name}`}
                data-testid={`paginated-param-${p.name}`}
                type={p.type === "date" ? "date" : "text"}
                value={values[p.name] ?? ""}
                onChange={e => setValue(p.name, e.target.value)}
                style={inputStyle}
              />
            )}
          </div>
        ))}
        <button
          type="submit"
          data-testid="paginated-generate-btn"
          disabled={state.kind === "generating" || missingRequired.length > 0}
          title={missingRequired.length > 0 ? `Required: ${missingRequired.join(", ")}` : undefined}
          style={{
            padding: "0.5rem 1.1rem",
            background: state.kind === "generating" || missingRequired.length > 0 ? "#9ac4ea" : "#0078d4",
            color: "white",
            border: "none",
            borderRadius: "4px",
            fontSize: "0.9rem",
            fontWeight: 600,
            cursor: state.kind === "generating" || missingRequired.length > 0 ? "not-allowed" : "pointer"
          }}
        >
          {state.kind === "generating" ? "Generating…" : "Generate PDF"}
        </button>
        {state.kind === "preview" && (
          <a
            href={state.url}
            download={state.filename}
            data-testid="paginated-download"
            style={{
              padding: "0.5rem 1.1rem",
              background: "white",
              color: "#0078d4",
              border: "1px solid #0078d4",
              borderRadius: "4px",
              fontSize: "0.9rem",
              fontWeight: 600,
              textDecoration: "none"
            }}
          >
            ↓ Download
          </a>
        )}
      </form>

      <div style={{ flex: 1, minHeight: 0, marginTop: "0.75rem" }}>
        {state.kind === "idle" && (
          <p style={{ color: "#555" }} data-testid="paginated-idle">
            Set the parameters above and choose <strong>Generate PDF</strong> to preview the report before sharing it.
          </p>
        )}
        {state.kind === "generating" && (
          <p style={{ color: "#555" }} data-testid="paginated-generating">
            Generating PDF… this can take up to ~2 minutes for a large report.
          </p>
        )}
        {state.kind === "stub" && (
          <div data-testid="paginated-stub" style={{ color: "#8a6d00", background: "#fff7e0", border: "1px solid #f0d98a", borderRadius: "6px", padding: "1rem", maxWidth: "640px" }}>
            <strong>This report isn't available yet.</strong>
            <p style={{ margin: "0.5rem 0 0", fontSize: "0.9rem" }}>
              The export mechanism is live, but this report is still being published.
              It will become available once setup is complete — no action needed on your part.
            </p>
          </div>
        )}
        {state.kind === "error" && (
          <p style={{ color: "#b00020" }} data-testid="paginated-error">
            Couldn't generate the PDF: {state.message}
          </p>
        )}
        {state.kind === "preview" && (
          <object
            data={state.url}
            type="application/pdf"
            data-testid="paginated-preview"
            style={{ width: "100%", height: "100%", border: "1px solid #ddd", borderRadius: "4px" }}
          >
            <p style={{ color: "#555" }}>
              PDF preview isn't supported in this browser.{" "}
              <a href={state.url} download={state.filename}>Download the PDF</a> instead.
            </p>
          </object>
        )}
      </div>
    </section>
  );
}
